import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Video, MapPin, Users, ChevronRight, ChevronLeft, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { SITE } from "../data/site";

const TYPES = [
  { key: "Online Video Session", label: "Individual · Online", sub: "Over secure video · 1 hour", price: "₹800", icon: Video },
  { key: "In-Person · Jubilee Hills", label: "Individual · In-Person", sub: "At the Jubilee Hills studio · 1 hour", price: "₹1,000", icon: MapPin },
  { key: "Couples Therapy · Online", label: "Couples · Online", sub: "For two, over secure video · 1 hour", price: "₹800", icon: Users },
  { key: "Couples Therapy · In-Person", label: "Couples · In-Person", sub: "For two, at the studio · 1 hour", price: "₹1,000", icon: Users },
];
const periodOf = (t) => {
  let h = parseInt(t.split(":")[0], 10);
  const pm = t.includes("PM");
  if (pm && h !== 12) h += 12;
  if (!pm && h === 12) h = 0;
  if (h < 12) return "Morning";
  if (h < 17) return "Afternoon";
  return "Evening";
};
const groupSlots = (list) => list.reduce((acc, t) => { const p = periodOf(t); (acc[p] = acc[p] || []).push(t); return acc; }, {});

const todayStr = () => new Date().toISOString().split("T")[0];

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export const BookingModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ consultation_type: "", date: "", time_slot: "", name: "", email: "", phone: "", concern: "", consent: false });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    if (!open || !form.date) { setSlots(null); return; }
    let live = true;
    setSlotsLoading(true);
    api.get(`/availability?date=${form.date}`)
      .then((r) => { if (live) setSlots(r.data.slots); })
      .catch(() => { if (live) setSlots([]); })
      .finally(() => { if (live) setSlotsLoading(false); });
    return () => { live = false; };
  }, [form.date, open]);

  const reset = () => { setStep(0); setDone(false); setForm({ consultation_type: "", date: "", time_slot: "", name: "", email: "", phone: "", concern: "", consent: false }); };
  const close = () => { onClose(); setTimeout(reset, 300); };

  const canNext = () => {
    if (step === 0) return !!form.consultation_type;
    if (step === 1) return !!form.date && !!form.time_slot;
    return true;
  };

  const submit = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) return toast.error("Please enter your name");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Please enter a valid email");
    if (!/^\d{7,15}$/.test(form.phone.replace(/\s/g, ""))) return toast.error("Please enter a valid phone number");
    if (form.concern.trim().length < 2) return toast.error("Please share a brief note on your focus");
    if (!form.consent) return toast.error("Please accept the privacy consent to continue");
    setLoading(true);
    try {
      const res = await api.post("/appointments", { ...form, phone: form.phone.replace(/\s/g, "") });
      const { appointment, order_id, key_id, amount } = res.data;
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load the payment gateway. Please try again."); return; }
      setLoading(false);
      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency: "INR",
        order_id,
        name: "Serene Mind Place",
        description: form.consultation_type,
        prefill: { name: form.name, email: form.email, contact: form.phone.replace(/\s/g, "") },
        theme: { color: "#3A7D32" },
        handler: async (resp) => {
          setLoading(true);
          try {
            await api.post("/payments/verify", { appointment_id: appointment.id, ...resp });
            setDone(true);
            toast.success("Payment received! Your session request is confirmed.");
          } catch {
            toast.error("Payment verification failed — please contact us on 083176 34326.");
          } finally { setLoading(false); }
        },
        modal: { ondismiss: () => toast("Payment cancelled — your slot isn't reserved yet.") },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const selectedType = TYPES.find((t) => t.key === form.consultation_type);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] grid place-items-center p-4 bg-[#20301A]/50 backdrop-blur-sm" onClick={close}>
          <motion.div initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
            data-testid="booking-modal"
            className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-[28px] bg-[#F2F8E8] soft-shadow grid md:grid-cols-5">

            <div className="md:col-span-2 bg-[#3A7D32] text-[#F2F8E8] p-7 hidden md:flex flex-col justify-between">
              <div>
                <span className="overline text-[#FFC9A0]">Reserve a session</span>
                <h3 className="font-serif text-3xl mt-3 leading-tight">A first step, gently taken.</h3>
                <p className="text-sm text-[#C9DFAC] mt-4 leading-relaxed">Fill in a few details and Dr. Pratyusha will confirm your session over email.</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-4 mt-6">
                <div className="flex mb-1">{[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-[#E5A93C] text-[#E5A93C]" />)}</div>
                <p className="text-xs text-[#C9DFAC]">"Clarity on decision making — recommend everyone meet her at least once."</p>
              </div>
            </div>

            <div className="md:col-span-3 p-6 sm:p-8 relative">
              <button onClick={close} data-testid="booking-close" className="absolute top-5 right-5 text-[#55703F] hover:text-[#20301A]"><X size={20} /></button>

              {done ? (
                <div className="py-12 text-center" data-testid="booking-success">
                  <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-[#3A7D32] text-[#F2F8E8]"><Check size={30} /></div>
                  <h3 className="font-serif text-3xl mt-5 text-[#20301A]">Booked & paid</h3>
                  <p className="text-[#55703F] mt-3 max-w-sm mx-auto">Thank you, {form.name.split(" ")[0]}. Your payment is received and your slot is reserved. We've emailed a confirmation to <b>{form.email}</b> — Dr. Pratyusha will be in touch shortly.</p>
                  <button onClick={close} className="mt-7 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-7 py-3 text-sm hover:bg-[#20301A] transition-colors">Done</button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-7">
                    {[0, 1, 2].map((s) => (
                      <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s <= step ? "bg-[#3A7D32] w-8" : "bg-[#C9DFAC] w-4"}`} />
                    ))}
                    <span className="ml-2 text-xs font-mono text-[#55703F]">Step {step + 1} / 3</span>
                  </div>

                  {step === 0 && (
                    <div>
                      <h3 className="font-serif text-2xl text-[#20301A]">Choose your format</h3>
                      <div className="mt-5 grid gap-3">
                        {TYPES.map((t) => (
                          <button key={t.key} data-testid={`booking-type-${t.label.toLowerCase().replace(/ /g, "-")}`} onClick={() => set("consultation_type", t.key)}
                            className={`flex items-center gap-4 rounded-2xl border p-4 text-left transition-all ${form.consultation_type === t.key ? "border-[#3A7D32] bg-[#DFF0C4]" : "border-[#C9DFAC] hover:border-[#3A7D32]"}`}>
                            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#FFEFDF] text-[#3A7D32]"><t.icon size={18} /></span>
                            <div className="flex-1"><div className="font-medium text-[#20301A]">{t.label}</div><div className="text-xs text-[#55703F]">{t.sub}</div></div>
                            <span className="font-serif text-lg text-[#3A7D32]">{t.price}</span>
                            {form.consultation_type === t.key && <Check className="text-[#3A7D32]" size={18} />}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div>
                      <h3 className="font-serif text-2xl text-[#20301A]">Pick a date & time</h3>
                      <label className="block mt-5 text-xs font-mono uppercase tracking-widest text-[#55703F] mb-2">Preferred date</label>
                      <input data-testid="booking-form-date-picker" type="date" min={todayStr()} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value, time_slot: "" }))}
                        className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 text-[#20301A] outline-none focus:border-[#3A7D32]" />
                      {!form.date ? (
                        <p className="mt-5 text-sm text-[#55703F]" data-testid="booking-slots-hint">Select a date to see Dr. Pratyusha's open times.</p>
                      ) : slotsLoading ? (
                        <div className="mt-6 flex items-center gap-2 text-sm text-[#55703F]" data-testid="booking-slots-loading"><Loader2 size={15} className="animate-spin" /> Checking availability…</div>
                      ) : slots && slots.length === 0 ? (
                        <p className="mt-5 text-sm text-[#B0500F]" data-testid="booking-no-slots">No open slots on this date — please pick another day.</p>
                      ) : (
                        Object.entries(groupSlots(slots || [])).map(([period, times]) => (
                        <div key={period} className="mt-5">
                          <div className="text-xs font-mono uppercase tracking-widest text-[#55703F] mb-2">{period}</div>
                          <div className="flex flex-wrap gap-2">
                            {times.map((t) => (
                              <button key={t} data-testid={`booking-slot-${t.replace(/[: ]/g, "")}`} onClick={() => set("time_slot", `${period} · ${t}`)}
                                className={`rounded-full px-4 py-2 text-sm border transition-all ${form.time_slot === `${period} · ${t}` ? "bg-[#3A7D32] text-[#F2F8E8] border-[#3A7D32]" : "border-[#C9DFAC] text-[#55703F] hover:border-[#3A7D32]"}`}>{t}</button>
                            ))}
                          </div>
                        </div>
                        ))
                      )}
                    </div>
                  )}

                  {step === 2 && (
                    <div>
                      <h3 className="font-serif text-2xl text-[#20301A]">Your details</h3>
                      <div className="mt-5 space-y-3">
                        <input data-testid="booking-form-name-input" placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)}
                          className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#3A7D32]" />
                        <input data-testid="booking-form-email-input" type="email" placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)}
                          className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#3A7D32]" />
                        <input data-testid="booking-form-phone-input" placeholder="Phone number" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                          className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#3A7D32]" />
                        <textarea data-testid="booking-form-concern-input" rows={3} placeholder="What would you like to focus on? (brief note)" value={form.concern} onChange={(e) => set("concern", e.target.value)}
                          className="w-full rounded-xl border border-[#C9DFAC] bg-white px-4 py-3 outline-none focus:border-[#3A7D32] resize-none" />
                        <label className="flex items-start gap-2.5 rounded-xl bg-[#DFF0C4]/50 border border-[#C9DFAC] p-3.5 cursor-pointer">
                          <input type="checkbox" data-testid="booking-consent-checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)}
                            className="mt-0.5 w-4 h-4 accent-[#3A7D32] shrink-0" />
                          <span className="text-xs text-[#55703F] leading-relaxed">
                            I consent to Serene Mind Place storing the details I share here so Dr. Pratyusha can contact me about my session.
                            Everything you share is <b className="text-[#20301A]">strictly confidential</b> — used only for your care, never sold or shared with third parties, and you may ask for it to be deleted at any time.
                          </span>
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="mt-7 flex items-center justify-between">
                    <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
                      className={`inline-flex items-center gap-1 text-sm ${step === 0 ? "opacity-0 pointer-events-none" : "text-[#55703F] hover:text-[#20301A]"}`}>
                      <ChevronLeft size={16} /> Back
                    </button>
                    {step < 2 ? (
                      <button data-testid="booking-next" onClick={() => canNext() ? setStep(step + 1) : toast.error("Please complete this step")}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-6 py-3 text-sm hover:bg-[#20301A] transition-colors disabled:opacity-40" disabled={!canNext()}>
                        Continue <ChevronRight size={16} />
                      </button>
                    ) : (
                      <button data-testid="booking-form-submit-button" onClick={submit} disabled={loading}
                        className="inline-flex items-center gap-2 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-6 py-3 text-sm hover:bg-[#20301A] transition-colors disabled:opacity-60">
                        {loading ? <><Loader2 size={16} className="animate-spin" /> Processing…</> : <>Pay {selectedType?.price} & Book <Check size={16} /></>}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
